import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Filter } from "lucide-react";
import { getProducts, getCategories } from "../lib/api";

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(
    searchParams.get("category") || ""
  );

  // Load categories once
  useEffect(() => {
    getCategories()
      .then(setCategories)
      .catch((err) => console.error("Categories error:", err));
  }, []);

  // Load products when filters change
  useEffect(() => {
    getProducts({
      category: selectedCategory,
      search: searchTerm,
    })
      .then(setProducts)
      .catch((err) => console.error("Products error:", err));
  }, [selectedCategory, searchTerm]);

  const handleCategoryChange = (slug) => {
    setSelectedCategory(slug);
    slug ? setSearchParams({ category: slug }) : setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Page Header */}
      <div className="bg-white border-b border-[#E5E5E5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
            Our Products
          </h1>
          <p className="text-[#4A4A4A] max-w-2xl">
            Browse our complete collection of premium lighting solutions
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-white p-6 border border-[#E5E5E5] sticky top-24">
              <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                <Filter className="h-5 w-5" /> Filters
              </h3>

              {/* Search */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">
                  Search
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border-b border-[#E5E5E5] focus:border-[#BFA15F] bg-transparent px-0 py-2 focus:outline-none"
                  />
                  <Search className="absolute right-0 top-2 h-5 w-5 text-[#888888]" />
                </div>
              </div>

              {/* Categories */}
              <div>
                <label className="block text-sm font-medium mb-3">
                  Categories
                </label>
                <div className="space-y-2">
                  <button
                    onClick={() => handleCategoryChange("")}
                    className={`w-full text-left px-3 py-2 text-sm ${
                      selectedCategory === ""
                        ? "bg-[#BFA15F] text-white"
                        : "hover:bg-[#F0F0ED]"
                    }`}
                  >
                    All Products
                  </button>

                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2 text-sm ${
                        selectedCategory === cat.slug
                          ? "bg-[#BFA15F] text-white"
                          : "hover:bg-[#F0F0ED]"
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            <div className="mb-6">
              <p className="text-[#4A4A4A]">
                {products.length} product
                {products.length !== 1 && "s"} found
              </p>
            </div>

            {products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#888888] text-lg">
                  No products found
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.map((product) => (
                  <Link
                    key={product.id}
                    to={`/products/${product.id}`}
                    className="group bg-white border hover:border-[#E5E5E5] p-4 transition"
                  >
                    <div className="relative h-64 mb-4 bg-[#F0F0ED] overflow-hidden">
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover"
                      />
                      {product.stock < 5 && (
                        <span className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1">
                          Low Stock
                        </span>
                      )}
                    </div>

                    <span className="text-xs text-[#BFA15F] uppercase">
                      {product.category_name}
                    </span>

                    <h3 className="font-heading text-lg font-semibold mt-1 mb-2 group-hover:text-[#BFA15F]">
                      {product.name}
                    </h3>

                    <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-3">
                      {product.description}
                    </p>

                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-[#BFA15F]">
                        NPR {product.price.toLocaleString()}
                      </span>
                      <span className="text-xs text-[#888888]">
                        View Details
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;
